import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { API_URL, getAuthToken } from '@/lib/api';
import { paths } from 'src/routes/paths';
import { useOrganization, useSyncTenantFromParams } from '@/context/organization-context';
import {
  useSocialAdsControllerGetAd,
  useSocialAdsControllerUpdateAd,
  useSocialAdsControllerExportAd,
} from '@/apis/backend/generated/social-ads/social-ads';
import {
  useMediaLibraryControllerUploadAsset,
  useMediaLibraryControllerRemoveBackgroundFromUrl,
} from '@/apis/backend/generated/media-library/media-library';
import {
  CanvasKitEditor,
  CanvasKitEditorProvider,
  type CanvasKitEditorHandle,
} from '@/components/canvas-editor/CanvasKitEditor';
import { ScenePropertiesPanel } from '@/components/canvas-editor/ScenePropertiesPanel';
import { CanvasSidebar, type SidebarTab } from '@/components/canvas-editor/canvas-sidebar/CanvasSidebar';
import { SceneTextTab } from '@/components/canvas-editor/canvas-sidebar/tabs/SceneTextTab';
import { PhotosTab } from '@/components/canvas-editor/canvas-sidebar/tabs/PhotosTab';
import { SceneElementsTab } from '@/components/canvas-editor/canvas-sidebar/tabs/SceneElementsTab';
import { SceneIconsTab } from '@/components/canvas-editor/canvas-sidebar/tabs/SceneIconsTab';
import { SceneLayersTab } from '@/components/canvas-editor/canvas-sidebar/tabs/SceneLayersTab';
import { ProductsTab } from '@/components/canvas-editor/canvas-sidebar/tabs/ProductsTab';
import { ComponentsTab } from '@/components/canvas-editor/canvas-sidebar/tabs/ComponentsTab';
import { SaveComponentDialog } from '@/components/canvas-editor/canvas-sidebar/tabs/SaveComponentDialog';
import { MediaPickerModal } from '@/components/email-generation/MediaPickerModal';
import { ProductPickerModal } from '@/components/email-generation/ProductPickerModal';
import { AiImageGenDialog } from '@/components/social-ads/AiImageGenDialog';
import { SocialAdChatPanel } from '@/components/social-ads/SocialAdChatPanel';
import { VariantStrip } from '@/components/social-ads/VariantStrip';
import { consumeSocialAdGenerationHandoff } from '@/components/social-ads/generation-handoff-store';
import type { MediaAssetDto, ProductVariantSearchResultDto } from '@/apis/backend/generated/models';
import type { SceneDocument } from '@/lib/scene-document';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import ChatIcon from '@mui/icons-material/ChatBubbleOutline';
import BrushIcon from '@mui/icons-material/Brush';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ImageIcon from '@mui/icons-material/Image';
import CategoryIcon from '@mui/icons-material/Category';
import LayersIcon from '@mui/icons-material/Layers';
import WidgetsIcon from '@mui/icons-material/Widgets';
import InterestsIcon from '@mui/icons-material/Interests';

export const Route = createFileRoute('/_authed/dashboard_/o/$orgId/c/$clientId/social-ads/$adId')({
  component: AdEditorPage,
});

type EditorMode = 'chat' | 'manual';

function AdEditorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { adId, orgId: routeOrgId, clientId: routeClientId } = Route.useParams();
  useSyncTenantFromParams(routeOrgId, routeClientId);
  const { activeOrganization, activeClient } = useOrganization();
  const tp = paths.dashboard.tenant(routeOrgId, routeClientId);

  const editorRef = useRef<CanvasKitEditorHandle>(null);
  const [mode, setMode] = useState<EditorMode>('chat');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // In-flight initial generation. Set from the handoff store on mount when
  // we land here directly from /create (or persisted as a query refetch
  // when the worker writes the final canvas to the row). While active, the
  // page shows a small "Generating…" overlay on the canvas and feeds
  // canvas_update events from the SSE stream into the editor.
  const [activeGenerationId, setActiveGenerationId] = useState<string | null>(null);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [aiGenOpen, setAiGenOpen] = useState(false);
  const [saveComponentOpen, setSaveComponentOpen] = useState(false);
  const [saveComponentNodeIds, setSaveComponentNodeIds] = useState<string[]>([]);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const orgId = activeOrganization?.id || '';
  const clientId = activeClient?.id || '';

  const uploadMutation = useMediaLibraryControllerUploadAsset();
  const removeBackgroundMutation = useMediaLibraryControllerRemoveBackgroundFromUrl();

  const { data: ad, isLoading } = useSocialAdsControllerGetAd(
    orgId,
    clientId,
    adId,
    { query: { enabled: !!orgId && !!clientId && !!adId } },
  );

  const { mutate: updateAd, isPending: isSaving } = useSocialAdsControllerUpdateAd({
    mutation: {
      onSuccess: () => {
        setSnackbar({ open: true, message: 'Saved' });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/social-ads');
          },
        });
      },
      onError: () => setSnackbar({ open: true, message: 'Failed to save' }),
    },
  });

  const { mutate: exportAd, isPending: isExporting } = useSocialAdsControllerExportAd({
    mutation: {
      onSuccess: (data) => {
        setSnackbar({ open: true, message: 'Exported successfully' });
        if (data.thumbnailUrl) {
          const a = document.createElement('a');
          a.href = data.thumbnailUrl;
          a.download = `${ad?.name || 'social-ad'}.png`;
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/social-ads');
          },
        });
      },
      onError: () => setSnackbar({ open: true, message: 'Export failed' }),
    },
  });

  const initialDocument = useMemo(() => {
    if (!ad?.canvasJson) return null;
    return ad.canvasJson as Record<string, any> as SceneDocument;
  }, [ad?.canvasJson]);

  // ── Callbacks ───────────────────────────────────────────────────────
  const handleModified = useCallback(() => {
    if (!orgId || !clientId || !adId) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const doc = editorRef.current?.toJSON();
      if (!doc) return;
      updateAd({
        orgId,
        clientId,
        id: adId,
        data: { canvasJson: doc as any },
      });
    }, 2000);
  }, [orgId, clientId, adId, updateAd]);

  const handleSave = useCallback(() => {
    if (!orgId || !clientId || !adId || !editorRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    const doc = editorRef.current.toJSON();
    if (!doc) return;
    updateAd({ orgId, clientId, id: adId, data: { canvasJson: doc as any } });
  }, [orgId, clientId, adId, updateAd]);

  const handleExport = useCallback(() => {
    if (!orgId || !clientId || !adId || !editorRef.current) return;
    const dataUrl = editorRef.current.toDataURL({ pixelRatio: 2 });
    if (!dataUrl) return;
    exportAd({ orgId, clientId, id: adId, data: { dataUrl } });
  }, [orgId, clientId, adId, exportAd]);

  const handleRemoveBackground = useCallback(
    async (imageSrc: string): Promise<string | null> => {
      if (!orgId || !clientId) return null;
      try {
        const result = await removeBackgroundMutation.mutateAsync({
          orgId,
          clientId,
          data: { imageUrl: imageSrc },
        });
        return result.publicUrl ?? null;
      } catch {
        return null;
      }
    },
    [orgId, clientId, removeBackgroundMutation],
  );

  const handleMediaSelect = useCallback((assets: MediaAssetDto[]) => {
    const url = assets[0]?.publicUrl;
    if (url) editorRef.current?.addImage(url);
  }, []);

  const handleProductSelect = useCallback((products: ProductVariantSearchResultDto[]) => {
    const imageUrl = products[0]?.productImages?.[0]?.src;
    if (imageUrl) editorRef.current?.addImage(imageUrl);
  }, []);

  const handleAiImageReady = useCallback((url: string) => {
    editorRef.current?.addImage(url);
  }, []);

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !orgId || !clientId) return;
      e.target.value = '';
      try {
        const result = await uploadMutation.mutateAsync({
          orgId,
          clientId,
          data: { file, category: 'custom' },
        });
        const url = (result as any).publicUrl || (result as any).data?.publicUrl;
        if (url) editorRef.current?.addImage(url);
      } catch {
        /* ignore */
      }
    },
    [orgId, clientId, uploadMutation],
  );

  // Chat agent finished a turn — push the new canvas into the editor + persist.
  const handleAgentCanvasUpdate = useCallback(
    (canvas: SceneDocument) => {
      editorRef.current?.loadDocument(canvas);
      // Persist immediately so a refresh or variant generation sees the new state.
      if (orgId && clientId && adId) {
        updateAd({
          orgId,
          clientId,
          id: adId,
          data: { canvasJson: canvas as any },
        });
      }
    },
    [orgId, clientId, adId, updateAd],
  );

  // Refresh the editor's document when the underlying ad row changes id
  // (variant strip navigation re-renders this component with a new adId, but
  // the editor only reads initialDocument once).
  useEffect(() => {
    if (initialDocument) {
      editorRef.current?.loadDocument(initialDocument);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adId]);

  // Handoff from /dashboard/social-ads/create: the create surface stashed
  // the in-flight generationId in the handoff store and navigated here.
  // Pick it up once on mount per adId so a focus swap doesn't re-fire the
  // handoff. Mirrors EmailChatSurface's consumeChatHandoff path.
  const handoffFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (handoffFiredRef.current === adId) return;
    handoffFiredRef.current = adId;
    const handoff = consumeSocialAdGenerationHandoff(adId);
    if (handoff?.generationId) {
      setActiveGenerationId(handoff.generationId);
      setGenerationStatus('Generating…');
      setGenerationError(null);
    }
  }, [adId]);

  // Subscribe to the in-flight generation's Redis-backed SSE stream and
  // feed canvas_update events into the editor. Uses Last-Event-ID resume
  // so a tab refresh during generation picks up the same stream where it
  // left off (worker stays running regardless of client disconnect).
  useEffect(() => {
    if (!activeGenerationId || !orgId || !clientId) return;
    const token = getAuthToken();
    if (!token) return;

    const url = `${API_URL}/api/v1/${orgId}/${clientId}/social-ads/generations/${activeGenerationId}/stream`;
    const ac = new AbortController();
    let lastEventId = '0';
    let sawError = false;

    class FatalSubscribeError extends Error {}

    (async () => {
      try {
        await fetchEventSource(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Last-Event-ID': lastEventId,
          },
          signal: ac.signal,
          openWhenHidden: true,
          async onopen(res) {
            if (!res.ok) {
              throw new FatalSubscribeError(`Stream open failed: ${res.status}`);
            }
          },
          onmessage(ev) {
            if (ev.id) lastEventId = ev.id;
            if (!ev.data) return;
            let event: { type: string; data?: any };
            try {
              event = JSON.parse(ev.data);
            } catch {
              return;
            }
            if (event.type === 'status') {
              setGenerationStatus((event.data?.message as string) ?? null);
            } else if (event.type === 'complete') {
              // Apply the final canvas directly so we don't wait on a
              // network round-trip to refetch the ad row.
              const canvas = event.data?.canvasJson as SceneDocument | undefined;
              if (canvas) editorRef.current?.loadDocument(canvas);
              setGenerationStatus(null);
              setActiveGenerationId(null);
              queryClient.invalidateQueries({
                predicate: (q) => {
                  const k = q.queryKey[0];
                  return typeof k === 'string' && k.includes('/social-ads');
                },
              });
            } else if (event.type === 'error') {
              // Keep activeGenerationId set so the streaming splash stays
              // visible with the error message — clearing it would let the
              // page fall through to "No canvas data" since the worker
              // never wrote canvasJson into the draft row.
              sawError = true;
              setGenerationError((event.data?.message as string) ?? 'Generation failed');
              setGenerationStatus(null);
            } else if (event.type === 'stream_close') {
              setGenerationStatus(null);
              // Only clear activeGenerationId on clean close — on error
              // keep the splash mounted so the user sees what went wrong
              // instead of a dead-end blank canvas.
              if (!sawError) {
                setActiveGenerationId(null);
              }
              queryClient.invalidateQueries({
                predicate: (q) => {
                  const k = q.queryKey[0];
                  return typeof k === 'string' && k.includes('/social-ads');
                },
              });
              throw new FatalSubscribeError('__stream_closed__');
            }
          },
          onerror(err) {
            throw err;
          },
        });
      } catch (err) {
        // Stream_closed is the normal exit. Anything else is logged but
        // doesn't surface — the row will refetch via the invalidate above.
        const msg = err instanceof Error ? err.message : String(err);
        if (msg !== '__stream_closed__') {
          // eslint-disable-next-line no-console
          console.warn('[social-ad] in-flight stream ended:', msg);
        }
      }
    })();

    return () => {
      ac.abort();
    };
  }, [activeGenerationId, orgId, clientId, queryClient]);

  // ── Sidebar tabs (manual mode only) ─────────────────────────────────
  const sidebarTabs: SidebarTab[] = useMemo(
    () =>
      [
        {
          id: 'text',
          label: 'Text',
          icon: TextFieldsIcon,
          content: <SceneTextTab editorRef={editorRef} />,
        },
        {
          id: 'elements',
          label: 'Elements',
          icon: CategoryIcon,
          content: <SceneElementsTab editorRef={editorRef} />,
        },
        {
          id: 'icons',
          label: 'Icons',
          icon: InterestsIcon,
          content: <SceneIconsTab />,
        },
        ...(clientId
          ? [
              {
                id: 'components',
                label: 'Library',
                icon: WidgetsIcon,
                content: (
                  <ComponentsTab
                    orgId={orgId}
                    clientId={clientId}
                    editorRef={editorRef}
                  />
                ),
              },
            ]
          : []),
        {
          id: 'media',
          label: 'Media',
          icon: ImageIcon,
          content: (
            <PhotosTab
              orgId={orgId}
              clientId={clientId}
              onImageClick={(url) => editorRef.current?.addImage(url)}
              onUploadFromComputer={() => fileInputRef.current?.click()}
              onOpenAIImageGen={() => setAiGenOpen(true)}
            />
          ),
        },
        ...(clientId
          ? [
              {
                id: 'products',
                label: 'Products',
                icon: ShoppingBagIcon,
                content: (
                  <ProductsTab
                    clientId={clientId}
                    onProductSelect={(product: ProductVariantSearchResultDto) => {
                      const imageUrl = product.productImages?.[0]?.src;
                      if (imageUrl) editorRef.current?.addImage(imageUrl);
                    }}
                  />
                ),
              },
            ]
          : []),
        {
          id: 'layers',
          label: 'Layers',
          icon: LayersIcon,
          content: (
            <SceneLayersTab
              onSaveAsComponent={(nodeIds) => {
                setSaveComponentNodeIds(nodeIds);
                setSaveComponentOpen(true);
              }}
            />
          ),
        },
      ] as SidebarTab[],
    [clientId, orgId],
  );

  // ── Loading / Error states ──────────────────────────────────────────
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!ad?.canvasJson || !initialDocument) {
    // Two flavors:
    //   1. We just landed from /create and the worker hasn't written the
    //      canvas yet — show the streaming hand-off splash. Once the SSE
    //      stream's `complete` event fires we'll re-render with the doc.
    //   2. The ad has no canvas at all (rare; old row, manual delete) —
    //      show a real empty state with a back button.
    if (activeGenerationId) {
      const hasError = !!generationError;
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: 2,
            px: 3,
          }}
        >
          {hasError ? (
            <>
              <Typography variant="h6" color="error" textAlign="center">
                Generation failed
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, textAlign: 'center' }}>
                {generationError}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate({ to: tp.socialAds })}
                  sx={{ textTransform: 'none' }}
                >
                  Back to Social Ads
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate({ to: tp.socialAdsCreate })}
                  sx={{ textTransform: 'none' }}
                >
                  Try again
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <CircularProgress size={32} />
              <Typography variant="h6" color="text.secondary">
                {generationStatus ?? 'Designing your ad…'}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Saved to your library — safe to refresh.
              </Typography>
            </>
          )}
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No canvas data found for this ad
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: tp.socialAds })}>
          Back to Social Ads
        </Button>
      </Box>
    );
  }

  return (
    <Box
      data-testid="social-ads-editor"
      sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 56,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton size="small" onClick={() => navigate({ to: tp.socialAds })}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ maxWidth: 300 }}>
            {ad.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {ad.width}x{ad.height}
          </Typography>
          {ad.masterAdId && (
            <Typography
              variant="caption"
              sx={{
                ml: 0.5,
                px: 0.75,
                py: 0.15,
                borderRadius: 0.5,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Variant
            </Typography>
          )}
        </Stack>

        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, v: EditorMode | null) => v && setMode(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
        >
          <ToggleButton value="chat">
            <ChatIcon fontSize="small" sx={{ mr: 0.5 }} />
            Chat
          </ToggleButton>
          <ToggleButton value="manual">
            <BrushIcon fontSize="small" sx={{ mr: 0.5 }} />
            Manual
          </ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip
            title={
              <Box sx={{ fontSize: '12px', lineHeight: 1.6 }}>
                <strong>Canvas shortcuts</strong>
                <br />
                Ctrl/Cmd + Scroll -- Zoom in/out
                <br />
                Scroll -- Pan
                <br />
                Space + Drag -- Pan
                <br />
                Click -- Select object
                <br />
                Shift + Click -- Multi-select
                <br />
                Double-click -- Enter group
                <br />
                Delete/Backspace -- Delete selected
                <br />
                Ctrl/Cmd + Z -- Undo
                <br />
                Ctrl/Cmd + Shift + Z -- Redo
                <br />
                Ctrl/Cmd + C/V -- Copy/Paste
                <br />
                Ctrl/Cmd + D -- Duplicate
                <br />
                Ctrl/Cmd + G -- Group
                <br />
                Ctrl/Cmd + Shift + G -- Ungroup
              </Box>
            }
            arrow
            placement="bottom-end"
          >
            <IconButton size="small" sx={{ color: 'text.secondary' }}>
              <HelpIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {mode === 'manual' && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<WidgetsIcon />}
              onClick={() => {
                const store = editorRef.current?.getStore();
                if (!store) return;
                const ids = Array.from(store.getState().selectedIds);
                if (ids.length === 0) return;
                setSaveComponentNodeIds(ids);
                setSaveComponentOpen(true);
              }}
              sx={{ textTransform: 'none' }}
            >
              Save Component
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={isSaving ? <CircularProgress size={14} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={isSaving}
            sx={{ textTransform: 'none' }}
            data-testid="social-ads-save"
          >
            Save
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={isExporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={isExporting}
            sx={{ textTransform: 'none' }}
          >
            Export PNG
          </Button>
        </Stack>
      </Box>

      {/* Editor Body */}
      <CanvasKitEditorProvider initialDocument={initialDocument}>
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {mode === 'chat' ? (
            <Box
              sx={{
                width: 380,
                flexShrink: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <SocialAdChatPanel
                orgId={orgId}
                clientId={clientId}
                adId={adId}
                onCanvasUpdate={handleAgentCanvasUpdate}
              />
            </Box>
          ) : (
            <CanvasSidebar tabs={sidebarTabs} />
          )}

          <CanvasKitEditor
            ref={editorRef}
            initialDocument={initialDocument}
            onDocumentChange={handleModified}
          />

          {mode === 'manual' && (
            <Box
              sx={{
                width: 280,
                flexShrink: 0,
                borderLeft: '1px solid',
                borderColor: 'divider',
                overflow: 'auto',
              }}
            >
              <ScenePropertiesPanel onRemoveBackground={handleRemoveBackground} />
            </Box>
          )}
        </Box>
      </CanvasKitEditorProvider>

      <VariantStrip orgId={orgId} clientId={clientId} currentAdId={adId} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      <MediaPickerModal
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onSelect={handleMediaSelect}
        selectedAssets={[]}
      />
      <ProductPickerModal
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        onSelect={handleProductSelect}
        selectedProducts={[]}
        singleSelect
      />
      <AiImageGenDialog
        open={aiGenOpen}
        onClose={() => setAiGenOpen(false)}
        onImageReady={handleAiImageReady}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      <SaveComponentDialog
        open={saveComponentOpen}
        onClose={() => setSaveComponentOpen(false)}
        orgId={orgId}
        clientId={clientId}
        sourceCanvasId={adId}
        selectedNodeIds={saveComponentNodeIds}
      />
    </Box>
  );
}
